import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { getApplication, createOrUpdateApplication, getBusinessCategoriesAndTypes as getBusinessCategoriesAndTypesApi, getForteApplicationStatus, ForteApplicationStatus } from '../../../api/forteApplication';
import { getCountries as getCountriesApi } from '../../../api/country';
import { getUsStates, getStatesAndProvinces } from '../../../api/api';
import useLoadingOverlay from '../../../hooks/useLoadingOverlay';
export const ForteApplicationContext = createContext({
    currentApplication: null,
    parseCurrentStepFromApplication: () => 0,
    loading: true,
    error: null,
    saveApplication: async () => { },
    states: [{ Name: '', Code: '' }],
    statesAndProvinces: [{ Name: '', Code: '' }],
    countries: [{ Name: '', IsoCode: '' }],
    businessCategoriesAndTypes: {},
    owners: [],
    getApplicationStatus: async () => { },
    applicationStatus: null
});
export default function ForteApplicationProvider({ children }) {
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const [currentApplication, setCurrentApplication] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [states, setStates] = useState([
        { Name: '', Code: '' }
    ]);
    const [statesAndProvinces, setStatesAndProvinces] = useState([
        { Name: '', Code: '' }
    ]);
    const [countries, setCountries] = useState([
        { Name: '', IsoCode: '' }
    ]);
    const [businessCategoriesAndTypes, setBusinessCategoriesAndTypes] = useState({});
    const [owners, setOwners] = useState([]);
    const [applicationStatus, setApplicationStatus] = useState(null);
    const getCurrentApplication = useCallback(async () => {
        try {
            const requestedCurrentApplication = await getApplication();
            setCurrentApplication(requestedCurrentApplication);
            const requestedOwners = [
                requestedCurrentApplication.owner,
                requestedCurrentApplication.owner_2,
                requestedCurrentApplication.owner_3,
                requestedCurrentApplication.owner_4
            ].filter((owner) => owner);
            setOwners(requestedOwners);
        }
        catch (e) {
            setError(e.message);
        }
    }, []);
    const parseCurrentStepFromApplication = useCallback(() => {
        if (!currentApplication || !currentApplication.applicant_organization.dba_name) {
            return 0;
        }
        if (!currentApplication.applicant_organization.bank_routing_number) {
            return 1;
        }
        if (!currentApplication.owner.first_name ||
            applicationStatus?.Status === ForteApplicationStatus.Unsubmitted) {
            return 2;
        }
        return 3;
    }, [currentApplication, applicationStatus]);
    const requestStates = useCallback(async () => {
        try {
            const requestedStates = await getUsStates();
            setStates(requestedStates);
        }
        catch (e) {
            setError(e.message);
        }
    }, []);
    const requestStatesAndProvinces = useCallback(async () => {
        try {
            const requestedStatesAndProvinces = await getStatesAndProvinces();
            setStatesAndProvinces(requestedStatesAndProvinces);
        }
        catch (e) {
            setError(e.message);
        }
    }, []);
    const getCountries = useCallback(async () => {
        try {
            const requestedCountries = await getCountriesApi();
            setCountries(requestedCountries);
        }
        catch (e) {
            setError(e.message);
        }
    }, []);
    const getBusinessCategoriesAndTypes = useCallback(async () => {
        try {
            const requestedBusinessCategoriesAndTypes = await getBusinessCategoriesAndTypesApi();
            setBusinessCategoriesAndTypes(requestedBusinessCategoriesAndTypes);
        }
        catch (e) {
            setError(e.message);
        }
    }, []);
    const getApplicationStatus = useCallback(async () => {
        try {
            const statusDto = await getForteApplicationStatus();
            setApplicationStatus(statusDto);
        }
        catch (e) {
            setError(e.message);
        }
    }, []);
    useEffect(() => {
        (async () => {
            setError(null);
            setLoading(true);
            applyLoadingState();
            await getCurrentApplication();
            await requestStates();
            await requestStatesAndProvinces();
            await getCountries();
            await getBusinessCategoriesAndTypes();
            await getApplicationStatus();
            removeLoadingState();
            setLoading(false);
        })();
    }, [
        applyLoadingState,
        getCountries,
        getCurrentApplication,
        requestStates,
        requestStatesAndProvinces,
        removeLoadingState,
        getBusinessCategoriesAndTypes,
        getApplicationStatus
    ]);
    const saveApplication = useCallback(async (data) => {
        try {
            applyLoadingState();
            setLoading(true);
            await createOrUpdateApplication(data);
            await getCurrentApplication();
        }
        catch (e) {
            setError(e.message);
        }
        finally {
            setLoading(false);
            removeLoadingState();
        }
    }, [applyLoadingState, getCurrentApplication, removeLoadingState]);
    const contextValue = useMemo(() => ({
        currentApplication,
        parseCurrentStepFromApplication,
        loading,
        error,
        saveApplication,
        states,
        statesAndProvinces,
        countries,
        businessCategoriesAndTypes,
        owners,
        getApplicationStatus,
        applicationStatus
    }), [
        currentApplication,
        parseCurrentStepFromApplication,
        loading,
        error,
        saveApplication,
        states,
        statesAndProvinces,
        countries,
        businessCategoriesAndTypes,
        owners,
        getApplicationStatus,
        applicationStatus
    ]);
    return (_jsx(ForteApplicationContext.Provider, { value: contextValue, children: children }));
}
